import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import { useContext, useState } from "react";
import { Item, ItemActions, ItemContent } from "@volca/ui/components/ui/item";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import {
  ACTIVE_TEXT,
  ItemActionContext,
  sectionRowKeys,
} from "./statement-item-action";
import { LineItemRow } from "./statement-line-item-row";
import type { SectionBlock } from "./statement-schema";
import { SectionHeading } from "./statement-section-heading";

export const Section = ({ block }: { block: SectionBlock }) => {
  const collapsible = Boolean(block.total);
  const [open, setOpen] = useState(!collapsible);
  const action = useContext(ItemActionContext);
  const activeKey = action?.activeRowKey;
  const holdsActiveRow =
    Boolean(activeKey) && sectionRowKeys(block).includes(activeKey ?? "");
  const right = block.total
    ? block.total.value
    : `${block.items.length} ${block.items.length === 1 ? "entry" : "entries"}`;

  const items = open && (
    <div
      className={cn("w-full divide-y divide-line", {
        "pl-5": collapsible,
      })}
    >
      {block.items.map((item, index) => (
        <LineItemRow
          key={`${item.title}-${index}`}
          item={item}
          heading={block.heading}
        />
      ))}
    </div>
  );

  if (!collapsible) {
    return (
      <Stack align="start" gap={0} className="w-full gap-1">
        {block.heading && <SectionHeading left={block.heading} right={right} />}
        {items}
      </Stack>
    );
  }

  return (
    <Stack align="start" gap={0} className="w-full gap-1">
      <Item
        render={<button type="button" />}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex-nowrap items-start justify-between gap-2 p-0 text-left"
      >
        <ItemContent className="min-w-0 gap-0">
          {/* chevron on the same line as the name → vertically centered with it;
              caption + content are indented (pl-5) to align under the name */}
          <Stack direction="row" align="center" gap={2} className="min-w-0">
            <CaretRightIcon
              className={cn(
                "h-3 w-3 shrink-0 text-subtle transition-transform",
                { "rotate-90": open },
              )}
            />
            <Text
              variant="title"
              className="min-w-0 truncate"
              title={block.heading || undefined}
            >
              <span className={cn({ [ACTIVE_TEXT]: !open && holdsActiveRow })}>
                {block.heading ?? ""}
              </span>
            </Text>
          </Stack>
          {block.caption && (
            <p
              className="truncate pl-5 text-xs text-subtle"
              title={block.caption}
            >
              {block.caption}
            </p>
          )}
        </ItemContent>
        <ItemActions className="shrink-0">
          <Text variant="title">{right}</Text>
        </ItemActions>
      </Item>
      {items}
    </Stack>
  );
};
